import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, actions } from "../../state/reducer";
import { useTable, useSortBy, Column } from "react-table";
import { TableBurgerIcon } from "../../icons/tableBurger";
import Paginator from "../paginator";
import { CellSortIcon } from "../../icons/sort";
import { taskBackend, TaskListItem } from "../../services";
import ShortManInfo from "../../components/ui/shortItem";
import { defPageSize } from "../../constants/events";
import { formatDateTimeStandard } from "../../utils";

interface TaskTableProps {
  columns: Column<TaskListItem>[];
  data: TaskListItem[];
  shorted?: boolean;
}

const tableGridTemplate = "1fr 2fr 2fr 2fr 2fr";

const UsersTable: React.FC<TaskTableProps> = ({ columns, data, shorted }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<TaskTableProps>({ columns, data }, useSortBy);

  return (
    <div {...getTableProps()} style={{ display: "grid", gap: "10px" }}>
      {/* Заголовок таблицы */}
      <div
        className="tableHeading"
        style={{
          display: "grid",
          gridTemplateColumns: shorted ? "1fr 1fr 1fr" : tableGridTemplate,
        }}
      >
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) => (
            <div
              {...column.getHeaderProps(column.getSortByToggleProps())}
              style={{ cursor: column.canSort ? "pointer" : "default" }}
            >
              {column.render("Header")}
              {column.Header !== "" && typeof column.Header === "string" ? (
                <CellSortIcon
                  width={8}
                  height={5}
                  classNames={
                    column.isSortedDesc
                      ? ["sortedDesc", "grayFilled"]
                      : ["grayFilled"]
                  }
                />
              ) : null}
            </div>
          ))
        )}
      </div>

      {/* Тело таблицы */}
      <div {...getTableBodyProps()} style={{ display: "grid", gap: "10px" }}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps()}
              style={{
                display: "grid",
                gridTemplateColumns: shorted
                  ? "1fr 1fr 1fr"
                  : tableGridTemplate,
                padding: "10px 0",
                borderBottom: "1px solid #ccc",
              }}
            >
              {row.cells.map((cell: any) => (
                <div {...cell.getCellProps()}>{cell.render("Cell")}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TasksListTable = (props: { search: string; shorted?: boolean }) => {
  const [totalCount, setTotalCount] = useState(0);
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();

  const data = State.taskData
  /* .filter((item) => {
    const searchRow = `${item?.author?.first_name || ""} ${
      item?.author?.last_name
    }`;
    return !props.search ? true : searchRow.indexOf(props.search) > -1;
  }); */

  useEffect(() => {
    taskBackend
      .getTasksCount(State.taskListRequestParams)
      .then((res) => {
        setTotalCount(res.count);
      })
      .catch((e) => {
        console.log("Error:", e);
      });
  }, [State.taskData]);

  const handlePageSelect = (data: { page: number; perPage: number }) => {
    const offset = data.page * data.perPage;
    const taskFilters = State.taskListRequestParams;
    dispatch(
      actions.taskRequestParams({
        ...taskFilters,
        offset,
        limit: data.perPage,
      })
    );
  };

  const columnsShorted: Column<TaskListItem[]>[] = [
    {
      Header: "Название",
      accessor: "name",
      canSort: true,
      Cell: ({ row }: any) => (
        <a
          href={`/tasks/detail/${row.original.id}`}
        >{`Задача №${row.original.id}`}</a>
      ),
    },
    {
      Header: "Крайний срок",
      accessor: "deadline",
      Cell: ({ row }: any) => (
        <div
          className={`userRoleTableText${
            !row.original.date_end ? " gray" : ""
          }`}
        >
          {row.original.date_end
            ? formatDateTimeStandard(row.original.date_end)
            : "Без срока"}
        </div>
      ),
    },
    {
      Header: "Потановщик",
      accessor: "evecuter",
      Cell: ({ row }: any) => (
        <ShortManInfo
          photo={
            row.original.executer?.photoUrl || row.original.author.photoUrl
          }
          phone={row.original.executer?.phone || row.original.author.phone}
          name={
            row.original.executer
              ? `${row.original.executer?.last_name} ${row.original.executer?.first_name}`
              : `${row.original.author?.last_name} ${row.original.author?.first_name}`
          }
        />
      ),
    },
  ];

  const columns: Column<TaskListItem[]>[] = [
    {
      Header: "Название",
      accessor: "name",
      canSort: true,
      Cell: ({ row }: any) => (
        <a
          href={`/tasks/detail/${row.original.id}`}
        >{`Задача №${row.original.id}`}</a>
      ),
    },
    {
      Header: "Дата создания",
      accessor: "activity_last_date",
      Cell: ({ row }: any) => (
        <div className="userRoleTableText green">
          {formatDateTimeStandard(row.original.date_start)}
        </div>
      ),
    },
    {
      Header: "Крайний срок",
      accessor: "deadline",
      Cell: ({ row }: any) => (
        <div
          className={`userRoleTableText${
            !row.original.date_end ? " gray" : ""
          }`}
        >
          {row.original.date_end
            ? formatDateTimeStandard(row.original.date_end)
            : "Без срока"}
        </div>
      ),
    },
    {
      Header: "Постановщик",
      accessor: "author",
      Cell: ({ row }: any) => (
        <ShortManInfo
          photo={row.original.author?.photoUrl}
          phone={row.original.author?.phone}
          name={`${row.original.author.last_name} ${row.original.author.first_name}`}
        />
      ),
    },
    {
      Header: "Исполнитель",
      accessor: "evecuter",
      Cell: ({ row }: any) => (
        <ShortManInfo
          photo={
            row.original.executer?.photoUrl || row.original.author.photoUrl
          }
          phone={row.original.executer?.phone || row.original.author.phone}
          name={
            row.original.executer
              ? `${row.original.executer?.last_name} ${row.original.executer?.first_name}`
              : `${row.original.author?.last_name} ${row.original.author?.first_name}`
          }
        />
      ),
    },
  ];

  return (
    <div className="tableContainer">
      <UsersTable
        data={data}
        columns={props.shorted ? columnsShorted : columns}
        shorted={!!props.shorted}
      />
      {totalCount > defPageSize && (
        <Paginator
          perPage={defPageSize}
          count={totalCount}
          onDataChange={handlePageSelect}
        />
      )}
    </div>
  );
};

export default TasksListTable;
